import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import axios from 'axios';
import { useUser } from '../UserContext';

import {
  APPROVED_SUPPORTER_COLORS,
  DEFAULT_SUPPORTER_COLOR,
  SUPPORTER_GRADIENT_DIRECTIONS,
  SUPPORTER_GRADIENT_PRESETS,
  canUseAnimatedSupporterStyles,
  canUseSupporterStyles,
  coerceSupporterColorForEntitlement,
  createCustomGradientToken,
  getApprovedColorById,
  getGradientBackground,
  parseCustomGradientToken,
  resolveSupporterStyle,
} from './supporterStyles';

import './SupporterMenu.css';

const iconOptions = [
  'none',
  'woodenReno',
  'jaraxxus',
  'razorpetal',
  'bananas',
  'annoy-o-tron',
  'smorc',
  'tinyfin',
  'common',
  'rare',
  'epic',
  'legendary',
  'boombot',
  'horde',
  'alliance',
  'snake',
  'boomS',
  'chicken',
  'wowr',
  'finleySmile',
  'reno',
  'illidon',
  'bob',
  'cat',
  'shrugillL',
  'shrugillR',
  'voidw',
  'blingtron',
  'dhguy',
  'rainbow',
  'faelin',
  'skelehead',
  'treeh',
  'ghoul',
  'pensive',
  'trumpet',
  'scared',
  'frogface',
  'thumbsup',
  'clowno',
  'fintective',
  'rafaamPog',
  'finking',
  'shocked',
  'waveflipper',
  'qnikkoHi',
  'treeLove',
  'qnikkoWhat',
  'qWOW',
  'qLOVE',
  'qCRY',
  'qCozy',
  'qCool',
  'qTPie',
  'qSleep',
  'starshine',
  'corncute',
  'snowflake',
  'gonk',
  'heartgem',
  'ghost',
  'pumpkin',
  '8murloc',
  'skelethead',
  'anger',
  'shARRk',
  'cheeseWink',
  'murComfy',
  'murComfyy',
  'murLaugh',
  'happyButtons',
  'lynessa',
  'gulpfingA',
];

const SupporterMenu = ({ show, handleClose }) => {
  const { user, setUser } = useUser();

  const entitlement = Number(
    user?.entitled ?? 0
  );

  const canUseStyles =
    canUseSupporterStyles(entitlement);

  const canUseAnimations =
    canUseAnimatedSupporterStyles(entitlement);

  const [supporterIcon, setSupporterIcon] =
    useState('none');

  const [supporterColor, setSupporterColor] =
    useState(DEFAULT_SUPPORTER_COLOR);

  const [gradientStart, setGradientStart] =
    useState('blue');

  const [gradientEnd, setGradientEnd] =
    useState('pink');

  const [gradientDirection, setGradientDirection] =
    useState('120');

  const [gradientAnimated, setGradientAnimated] =
    useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const activeRequestsRef = useRef(0);

  const currentSettingsRef = useRef({
    icon: 'none',
    color: DEFAULT_SUPPORTER_COLOR,
  });

  useEffect(() => {
    if (!user) {
      return;
    }

    const nextIcon = user.supIcon || 'none';

    const nextColor =
      coerceSupporterColorForEntitlement(
        user.supColor,
        entitlement
      );

    setSupporterIcon(nextIcon);
    setSupporterColor(nextColor);

    currentSettingsRef.current = {
      icon: nextIcon,
      color: nextColor,
    };

    const customGradient =
      parseCustomGradientToken(nextColor);

    if (customGradient) {
      setGradientStart(customGradient.start);
      setGradientEnd(customGradient.end);
      setGradientDirection(
        String(customGradient.direction)
      );

      setGradientAnimated(
        canUseAnimations &&
          customGradient.animated
      );
    } else {
      setGradientAnimated(false);
    }
  }, [
    user?.supIcon,
    user?.supColor,
    entitlement,
    canUseAnimations,
  ]);

  useEffect(() => {
    if (!canUseAnimations) {
      setGradientAnimated(false);
    }
  }, [canUseAnimations]);

  const selectedStyle = useMemo(() => {
    return resolveSupporterStyle(supporterColor);
  }, [supporterColor]);

  const customGradientToken = useMemo(() => {
    return createCustomGradientToken({
      start: gradientStart,
      end: gradientEnd,
      direction: gradientDirection,
      animated:
        canUseAnimations && gradientAnimated,
    });
  }, [
    gradientStart,
    gradientEnd,
    gradientDirection,
    gradientAnimated,
    canUseAnimations,
  ]);

  const customGradientStyle = useMemo(() => {
    return resolveSupporterStyle(
      customGradientToken
    );
  }, [customGradientToken]);

  const sendDataToServer = async (icon, color) => {
    activeRequestsRef.current += 1;

    setIsSaving(true);
    setSaveError('');

    try {
      const response = await axios.post(
        '/api/users/update-supporter.php',
        {
          icon,
          color,
        }
      );

      if (!response.data?.success) {
        throw new Error(
          response.data?.message ||
            'Unable to save supporter settings'
        );
      }
    } catch (error) {
      console.error(
        'Error saving supporter settings:',
        error
      );

      setSaveError(
        error.response?.data?.message ||
          error.message ||
          'Unable to save supporter settings'
      );
    } finally {
      activeRequestsRef.current -= 1;

      if (activeRequestsRef.current === 0) {
        setIsSaving(false);
      }
    }
  };

  const updateSettings = (icon, color) => {
    const allowedColor =
      coerceSupporterColorForEntitlement(
        color,
        entitlement
      );

    currentSettingsRef.current = {
      icon,
      color: allowedColor,
    };

    setUser((previousUser) => {
      if (!previousUser) {
        return previousUser;
      }

      return {
        ...previousUser,
        supIcon: icon === 'none' ? '' : icon,
        supColor: allowedColor,
      };
    });

    sendDataToServer(icon, allowedColor);
  };

  const selectIcon = (icon) => {
    setSupporterIcon(icon);

    updateSettings(
      icon,
      currentSettingsRef.current.color
    );
  };

  const selectColor = (color) => {
    const allowedColor =
      coerceSupporterColorForEntitlement(
        color,
        entitlement
      );

    setSupporterColor(allowedColor);

    updateSettings(
      currentSettingsRef.current.icon,
      allowedColor
    );
  };

  if (
    show === false ||
    !user ||
    !canUseStyles
  ) {
    return null;
  }

  const previewName =
    user.username ||
    user.name ||
    'Supporter Name';

  return (
    <div className="supporter-menu mt-3">
      <div className="supporter-menu-header">
        <div>
          <h3 className="supporter-menu-title">
            Supporter Style
          </h3>

          <p className="supporter-menu-description">
            Choose an icon and name style.
          </p>
        </div>

        {typeof handleClose === 'function' && (
          <button
            type="button"
            className="supporter-menu-close"
            aria-label="Close supporter menu"
            onClick={handleClose}
          >
            ×
          </button>
        )}
      </div>

      <div className="supporter-preview">
        <span className="supporter-preview-label">
          Preview
        </span>

        <div className="supporter-preview-user">
          {supporterIcon !== 'none' && (
            <img
              className="supporter-preview-icon"
              src={`/images/emotes/${supporterIcon}.png`}
              srcSet={[
                `/images/emotes/${supporterIcon}.png 1x`,
                `/images/emotes/${supporterIcon}2.png 2x`,
                `/images/emotes/${supporterIcon}3.png 4x`,
              ].join(', ')}
              alt=""
            />
          )}

          <span
            className={[
              'supporter-name',
              selectedStyle.type === 'gradient'
                ? 'supporter-name--gradient'
                : '',
              selectedStyle.animated
                ? 'supporter-name--animated'
                : '',
            ]
              .filter(Boolean)
              .join(' ')}
            style={
              selectedStyle.type === 'solid'
                ? {
                    color: selectedStyle.value,
                  }
                : selectedStyle.type ===
                    'gradient'
                  ? {
                      backgroundImage:
                        getGradientBackground(
                          selectedStyle
                        ),
                    }
                  : undefined
            }
          >
            {previewName}
          </span>
        </div>

        <div className="supporter-preview-details">
          <span>{selectedStyle.label}</span>

          {selectedStyle.animated && (
            <span>· Animated</span>
          )}
        </div>
      </div>

      <section className="supporter-section">
        <h4 className="supporter-section-title">
          User icon
        </h4>

        <div
          className="supporter-icon-options"
          role="radiogroup"
          aria-label="Supporter icon"
        >
          {iconOptions.map((icon) => {
            const isSelected =
              supporterIcon === icon;

            return (
              <button
                key={icon}
                type="button"
                className={[
                  'supporter-icon-option',
                  isSelected ? 'is-selected' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                role="radio"
                aria-checked={isSelected}
                aria-label={
                  icon === 'none'
                    ? 'No supporter icon'
                    : `Select ${icon} icon`
                }
                title={
                  icon === 'none'
                    ? 'No icon'
                    : icon
                }
                onClick={() => selectIcon(icon)}
              >
                {icon === 'none' ? (
                  <span
                    className="supporter-no-icon"
                    aria-hidden="true"
                  >
                    ×
                  </span>
                ) : (
                  <img
                    className="userIcon"
                    alt=""
                    src={`/images/emotes/${icon}.png`}
                    srcSet={[
                      `/images/emotes/${icon}.png 1x`,
                      `/images/emotes/${icon}2.png 2x`,
                      `/images/emotes/${icon}3.png 4x`,
                    ].join(', ')}
                  />
                )}
              </button>
            );
          })}
        </div>
      </section>

      <section className="supporter-section">
        <h4 className="supporter-section-title">
          Solid colors
        </h4>

        <div
          className="supporter-color-options"
          role="radiogroup"
          aria-label="Supporter name color"
        >
          <button
            type="button"
            className={[
              'supporter-color-option',
              supporterColor ===
              DEFAULT_SUPPORTER_COLOR
                ? 'is-selected'
                : '',
            ]
              .filter(Boolean)
              .join(' ')}
            role="radio"
            aria-checked={
              supporterColor ===
              DEFAULT_SUPPORTER_COLOR
            }
            aria-label="Use default name color"
            title="Default"
            onClick={() =>
              selectColor(DEFAULT_SUPPORTER_COLOR)
            }
          >
            <span className="supporter-default-swatch">
              ×
            </span>
          </button>

          {APPROVED_SUPPORTER_COLORS.map(
            (option) => {
              const isSelected =
                supporterColor ===
                option.value.toLowerCase();

              return (
                <button
                  key={option.id}
                  type="button"
                  className={[
                    'supporter-color-option',
                    isSelected
                      ? 'is-selected'
                      : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  role="radio"
                  aria-checked={isSelected}
                  aria-label={`Select ${option.label}`}
                  title={option.label}
                  onClick={() =>
                    selectColor(option.value)
                  }
                >
                  <span
                    className="supporter-color-swatch"
                    style={{
                      background: option.value,
                    }}
                  />
                </button>
              );
            }
          )}
        </div>
      </section>

      {canUseAnimations && (
        <section className="supporter-section">
          <h4 className="supporter-section-title">
            Animated gradient presets
          </h4>

          <div
            className="supporter-gradient-presets"
            role="radiogroup"
            aria-label="Animated gradient presets"
          >
            {SUPPORTER_GRADIENT_PRESETS.map(
              (preset) => {
                const isSelected =
                  supporterColor === preset.id;

                return (
                  <button
                    key={preset.id}
                    type="button"
                    className={[
                      'supporter-gradient-preset',
                      isSelected
                        ? 'is-selected'
                        : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    role="radio"
                    aria-checked={isSelected}
                    onClick={() =>
                      selectColor(preset.id)
                    }
                  >
                    <span
                      className={[
                        'supporter-gradient-swatch',
                        'is-animated',
                      ].join(' ')}
                      style={{
                        backgroundImage:
                          getGradientBackground({
                            ...preset,
                            type: 'gradient',
                          }),
                      }}
                    />

                    <span className="supporter-gradient-info">
                      <strong>{preset.label}</strong>
                      <small>Animated gradient</small>
                    </span>
                  </button>
                );
              }
            )}
          </div>
        </section>
      )}

      <section className="supporter-section supporter-gradient-builder">
        <div>
          <h4 className="supporter-section-title">
            Build your gradient
          </h4>

          <p className="supporter-section-description">
            Choose two colors and a direction.
          </p>
        </div>

        <div className="supporter-builder-controls">
          <label className="supporter-builder-field">
            <span>First color</span>

            <div className="supporter-select-row">
              <span
                className="supporter-select-swatch"
                style={{
                  background:
                    getApprovedColorById(
                      gradientStart
                    )?.value,
                }}
              />

              <select
                value={gradientStart}
                onChange={(event) =>
                  setGradientStart(
                    event.target.value
                  )
                }
              >
                {APPROVED_SUPPORTER_COLORS.map(
                  (option) => (
                    <option
                      key={option.id}
                      value={option.id}
                    >
                      {option.label}
                    </option>
                  )
                )}
              </select>
            </div>
          </label>

          <label className="supporter-builder-field">
            <span>Second color</span>

            <div className="supporter-select-row">
              <span
                className="supporter-select-swatch"
                style={{
                  background:
                    getApprovedColorById(
                      gradientEnd
                    )?.value,
                }}
              />

              <select
                value={gradientEnd}
                onChange={(event) =>
                  setGradientEnd(
                    event.target.value
                  )
                }
              >
                {APPROVED_SUPPORTER_COLORS.map(
                  (option) => (
                    <option
                      key={option.id}
                      value={option.id}
                    >
                      {option.label}
                    </option>
                  )
                )}
              </select>
            </div>
          </label>

          <label className="supporter-builder-field">
            <span>Direction</span>

            <select
              value={gradientDirection}
              onChange={(event) =>
                setGradientDirection(
                  event.target.value
                )
              }
            >
              {SUPPORTER_GRADIENT_DIRECTIONS.map(
                (option) => (
                  <option
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                )
              )}
            </select>
          </label>
        </div>

        {canUseAnimations && (
          <label className="supporter-motion-toggle">
            <input
              type="checkbox"
              checked={gradientAnimated}
              onChange={(event) =>
                setGradientAnimated(
                  event.target.checked
                )
              }
            />

            <span>Animate gradient</span>
          </label>
        )}

        <div className="supporter-custom-preview">
          <div
            className={[
              'supporter-custom-preview-background',
              canUseAnimations &&
              gradientAnimated
                ? 'is-animated'
                : '',
            ]
              .filter(Boolean)
              .join(' ')}
            style={{
              backgroundImage:
                getGradientBackground(
                  customGradientStyle
                ),
            }}
          />

          <span
            className={[
              'supporter-name',
              'supporter-name--gradient',
              canUseAnimations &&
              gradientAnimated
                ? 'supporter-name--animated'
                : '',
            ]
              .filter(Boolean)
              .join(' ')}
            style={{
              backgroundImage:
                getGradientBackground(
                  customGradientStyle
                ),
            }}
          >
            {previewName}
          </span>
        </div>

        <button
          type="button"
          className="supporter-use-gradient-button"
          disabled={
            supporterColor === customGradientToken
          }
          onClick={() =>
            selectColor(customGradientToken)
          }
        >
          {supporterColor === customGradientToken
            ? 'Gradient selected'
            : 'Use this gradient'}
        </button>
      </section>

      <div
        className="supporter-save-status"
        aria-live="polite"
      >
        {isSaving && (
          <span className="supporter-saving-text">
            Saving…
          </span>
        )}

        {!isSaving && saveError && (
          <span className="supporter-save-error">
            {saveError}
          </span>
        )}
      </div>
    </div>
  );
};

export default SupporterMenu;